import React, { useState } from 'react';
import './AdvancedApexBrochure.css';
import { useParams, useNavigate } from 'react-router-dom';


const AdvancedApexBrochure = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const navigate = useNavigate();


  const toggleWeek = (weekNumber) => {
    setExpandedWeek(expandedWeek === weekNumber ? null : weekNumber);
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "What prerequisites are required?",
      answer: "Platform Developer I certification or equivalent experience with Apex triggers, classes, and basic LWC."
    },
    {
      question: "Does this cover the PD II certification?",
      answer: "Yes, this course covers all topics needed for Platform Developer II certification with mock exams."
    },
    {
      question: "What kind of projects will we build?",
      answer: "You'll build complex applications including a custom CI/CD pipeline, enterprise integration solution, and advanced error handling framework."
    }
  ];

  return (
    <div className="course-brochure">
      <header className="brochure-header">
        <h1>Salesforce Advanced Apex Developer Program</h1>
        <p className="subtitle">Master enterprise-grade Apex development and prepare for Platform Developer II certification</p>
        <div className="header-highlights">
          <div className="highlight-item">
            <span className="highlight-number">8</span>
            <span className="highlight-label">Weeks Intensive</span>
          </div>
          <div className="highlight-item">
            <span className="highlight-number">100%</span>
            <span className="highlight-label">Exam Coverage</span>
          </div>
          <div className="highlight-item">
            <span className="highlight-number">4</span>
            <span className="highlight-label">Real Projects</span>
          </div>
        </div>
      </header>

      <div className="brochure-content">
        <div className="tabs">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'curriculum' ? 'active' : ''}`}
            onClick={() => setActiveTab('curriculum')}
          >
            Curriculum
          </button>
          <button 
            className={`tab-btn ${activeTab === 'outcomes' ? 'active' : ''}`}
            onClick={() => setActiveTab('outcomes')}
          >
            Career Outcomes
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-content">
              <h2>Why Advanced Apex Developers Are In Demand</h2>
              <div className="market-demand">
                <p>Enterprise Salesforce implementations require advanced Apex skills:</p>
                <ul>
                  <li><strong>82%</strong> of large Salesforce implementations report Apex customization needs</li>
                  <li><strong>45%</strong> higher salaries for developers with Platform Developer II certification</li>
                  <li><strong>3.5x</strong> more complex integrations now using Apex vs. 5 years ago</li>
                </ul>
              </div>

              <div className="program-details">
                <div className="detail-card">
                  <h3>What You'll Master</h3>
                  <ul>
                    <li>Advanced trigger patterns and framework design</li>
                    <li>Enterprise integration architectures</li>
                    <li>Custom metadata and configuration</li>
                    <li>Performance optimization at scale</li>
                    <li>Security and access patterns</li>
                  </ul>
                </div>

                <div className="detail-card">
                  <h3>Program Features</h3>
                  <ul>
                    <li>Daily code challenges and reviews</li>
                    <li>4 production-grade projects</li>
                    <li>PD II mock exams and review sessions</li>
                    <li>GitHub repository with best practice examples</li>
                    <li>Technical architecture workshops</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'curriculum' && (
            <div className="curriculum-content">
              <h2>8-Week Advanced Apex Curriculum</h2>
              <p className="cert-alert">Fully covers Platform Developer II certification requirements</p>
              
              <div className="week-accordion">
                {/* Week 1 */}
                <div className={`week-card ${expandedWeek === 1 ? 'active' : ''}`} onClick={() => toggleWeek(1)}>
                  <h3>Week 1: Advanced Trigger Frameworks</h3>
                  {expandedWeek === 1 && (
                    <div className="week-details">
                      <h4>Day 1-2: Trigger Design Patterns</h4>
                      <ul>
                        <li>Trigger handler framework architecture</li>
                        <li>Single trigger per object pattern</li>
                        <li>Trigger context variables deep dive</li>
                      </ul>
                      
                      <h4>Day 3-4: Bulkification Patterns</h4>
                      <ul>
                        <li>Collection patterns for bulk data</li>
                        <li>Governor limit aware coding</li>
                        <li>Asynchronous execution models</li>
                      </ul>
                      
                      <h4>Day 5: Unit Testing Framework</h4>
                      <ul>
                        <li>Test data factory patterns</li>
                        <li>Assertion best practices</li>
                        <li>Mocking external callouts</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Week 2 */}
                <div className={`week-card ${expandedWeek === 2 ? 'active' : ''}`} onClick={() => toggleWeek(2)}>
                  <h3>Week 2: Enterprise Integration</h3>
                  {expandedWeek === 2 && (
                    <div className="week-details">
                      <h4>Day 1-2: REST API Integration</h4>
                      <ul>
                        <li>HTTP callout implementation</li>
                        <li>OAuth 2.0 authentication flows</li>
                        <li>JSON serialization/deserialization</li>
                      </ul>
                      
                      <h4>Day 3-4: SOAP & Platform Events</h4>
                      <ul>
                        <li>WSDL generation and consumption</li>
                        <li>Platform event architecture</li>
                        <li>Change Data Capture patterns</li>
                      </ul>
                      
                      <h4>Day 5: Integration Framework</h4>
                      <ul>
                        <li>Retry mechanisms for failures</li>
                        <li>Queuing patterns with Queueable</li>
                        <li>Integration test strategies</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Week 3 */}
                <div className={`week-card ${expandedWeek === 3 ? 'active' : ''}`} onClick={() => toggleWeek(3)}>
                  <h3>Week 3: Asynchronous Processing</h3>
                  {expandedWeek === 3 && (
                    <div className="week-details">
                      <h4>Day 1-2: Queueable Apex</h4>
                      <ul>
                        <li>Chaining queueable jobs</li>
                        <li>Stateful transaction patterns</li>
                        <li>Monitoring async processes</li>
                      </ul>
                      
                      <h4>Day 3-4: Batch Apex</h4>
                      <ul>
                        <li>Batch class architecture</li>
                        <li>Scope sizing strategies</li>
                        <li>Error handling in batches</li>
                      </ul>
                      
                      <h4>Day 5: Scheduled Apex</h4>
                      <ul>
                        <li>Cron expression syntax</li>
                        <li>Dynamic scheduling patterns</li>
                        <li>Maintenance of scheduled jobs</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Week 4 */}
                <div className={`week-card ${expandedWeek === 4 ? 'active' : ''}`} onClick={() => toggleWeek(4)}>
                  <h3>Week 4: Custom Metadata & Settings</h3>
                  {expandedWeek === 4 && (
                    <div className="week-details">
                      <h4>Day 1-2: Custom Metadata Types</h4>
                      <ul>
                        <li>Metadata vs. custom objects</li>
                        <li>Deployment considerations</li>
                        <li>Hierarchical configuration patterns</li>
                      </ul>
                      
                      <h4>Day 3-4: Custom Settings</h4>
                      <ul>
                        <li>List vs. hierarchy custom settings</li>
                        <li>Cache invalidation strategies</li>
                        <li>Security implications</li>
                      </ul>
                      
                      <h4>Day 5: Feature Management</h4>
                      <ul>
                        <li>Feature toggle implementation</li>
                        <li>Release management patterns</li>
                        <li>Testing configurable features</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Week 5 */}
                <div className={`week-card ${expandedWeek === 5 ? 'active' : ''}`} onClick={() => toggleWeek(5)}>
                  <h3>Week 5: Security & Performance</h3>
                  {expandedWeek === 5 && (
                    <div className="week-details">
                      <h4>Day 1-2: Security Patterns</h4>
                      <ul>
                        <li>CRUD/FLS enforcement</li>
                        <li>Sharing calculation strategies</li>
                        <li>Transaction security policies</li>
                      </ul>
                      
                      <h4>Day 3-4: Performance Optimization</h4>
                      <ul>
                        <li>SOQL query optimization</li>
                        <li>Selective relationship queries</li>
                        <li>CPU time reduction techniques</li>
                      </ul>
                      
                      <h4>Day 5: Monitoring & Debugging</h4>
                      <ul>
                        <li>Transaction log analysis</li>
                        <li>Performance metrics collection</li>
                        <li>Error logging frameworks</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Week 6 */}
                <div className={`week-card ${expandedWeek === 6 ? 'active' : ''}`} onClick={() => toggleWeek(6)}>
                  <h3>Week 6: Advanced LWC Integration</h3>
                  {expandedWeek === 6 && (
                    <div className="week-details">
                      <h4>Day 1-2: Complex Apex Controllers</h4>
                      <ul>
                        <li>@AuraEnabled method patterns</li>
                        <li>Complex data type handling</li>
                        <li>Error propagation to UI</li>
                      </ul>
                      
                      <h4>Day 3-4: Cache Strategies</h4>
                      <ul>
                        <li>Platform cache implementation</li>
                        <li>Cache partitioning</li>
                        <li>Cache invalidation triggers</li>
                      </ul>
                      
                      <h4>Day 5: LWC Service Layer</h4>
                      <ul>
                        <li>Reusable service classes</li>
                        <li>Batch UI updates</li>
                        <li>Optimistic UI patterns</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Week 7 */}
                <div className={`week-card ${expandedWeek === 7 ? 'active' : ''}`} onClick={() => toggleWeek(7)}>
                  <h3>Week 7: DevOps & CI/CD</h3>
                  {expandedWeek === 7 && (
                    <div className="week-details">
                      <h4>Day 1-2: SFDX CLI Advanced</h4>
                      <ul>
                        <li>Metadata API commands</li>
                        <li>Package versioning</li>
                        <li>Scratch org management</li>
                      </ul>
                      
                      <h4>Day 3-4: CI/CD Pipelines</h4>
                      <ul>
                        <li>GitHub Actions for Salesforce</li>
                        <li>Jenkins pipeline configuration</li>
                        <li>Automated testing integration</li>
                      </ul>
                      
                      <h4>Day 5: Deployment Strategies</h4>
                      <ul>
                        <li>Change set alternatives</li>
                        <li>Sandbox promotion models</li>
                        <li>Hotfix deployment patterns</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Week 8 */}
                <div className={`week-card ${expandedWeek === 8 ? 'active' : ''}`} onClick={() => toggleWeek(8)}>
                  <h3>Week 8: Capstone & Certification</h3>
                  {expandedWeek === 8 && (
                    <div className="week-details">
                      <h4>Day 1-3: Enterprise Integration Project</h4>
                      <ul>
                        <li>Build end-to-end integration solution</li>
                        <li>Implement error handling framework</li>
                        <li>Performance test and optimize</li>
                      </ul>
                      
                      <h4>Day 4-5: PD II Exam Prep</h4>
                      <ul>
                        <li>Full-length practice exams</li>
                        <li>Exam question strategies</li>
                        <li>Key concept review sessions</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div className="projects-showcase">
                <h3>Enterprise-Grade Projects</h3>
                <div className="project-cards">
                  <div className="project-card">
                    <h4>Integration Framework</h4>
                    <ul>
                      <li>Custom REST API adapters</li>
                      <li>Automatic retry mechanism</li>
                      <li>Monitoring dashboard</li>
                    </ul>
                  </div>
                  <div className="project-card">
                    <h4>Error Handling System</h4>
                    <ul>
                      <li>Centralized error logging</li>
                      <li>Notification workflows</li>
                      <li>Self-healing patterns</li>
                    </ul>
                  </div>
                  <div className="project-card">
                    <h4>CI/CD Pipeline</h4>
                    <ul>
                      <li>Automated testing suite</li>
                      <li>Environment promotion</li>
                      <li>Release management</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'outcomes' && (
            <div className="outcomes-content">
              <h2>Career Paths for Advanced Apex Developers</h2>
              
              <div className="outcome-stats">
                <div className="stat-card">
                  <h3>Average Salary</h3>
                  <p className="stat-value">$135,000</p>
                  <p>for certified PD II developers in the US</p>
                </div>
                
                <div className="stat-card">
                  <h3>Job Growth</h3>
                  <p className="stat-value">58%</p>
                  <p>increase in advanced Apex roles since 2022</p>
                </div>
              </div>

              <h3>Career Opportunities</h3>
              <ul className="job-roles">
                <li>Salesforce Technical Architect</li>
                <li>Senior Apex Developer</li>
                <li>Integration Specialist</li>
                <li>DevOps Engineer (Salesforce)</li>
                <li>Solutions Engineer</li>
              </ul>

              <div className="testimonial">
                <blockquote>
                  "The advanced patterns I learned in this course helped me design an integration framework that reduced our error rate by 90% and got me promoted to Lead Developer."
                </blockquote>
                <p className="testimonial-author">- Maria S., Senior Salesforce Developer at TechCorp</p>
              </div>

              <div className="certification-path">
                <h3>Certification Pathway</h3>
                <div className="cert-cards">
                  <div className="cert-card">
                    <h4>Platform Developer II</h4>
                    <p>Full coverage of all exam objectives</p>
                    <ul>
                      <li>Enterprise integration</li>
                      <li>Performance optimization</li>
                      <li>Application lifecycle</li>
                    </ul>
                  </div>
                  <div className="cert-card">
                    <h4>JavaScript Developer I</h4>
                    <p>Complementary certification</p>
                    <ul>
                      <li>Advanced LWC concepts</li>
                      <li>Front-end integration</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-container">
            {faqs.map((faq, index) => (
              <div className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`} key={index}>
                <button className="faq-question" onClick={() => toggleFaq(index)}>
                  {faq.question}
                  <span className="toggle-icon">{expandedFaq === index ? '-' : '+'}</span>
                </button>
                {expandedFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="cta-section">
          <h2>Ready to Become an Advanced Apex Developer?</h2>
          <p>Next cohort begins: August 12, 2025</p>
          <button onClick={() => navigate('/enroll')} className="enroll-btn">Enroll Now</button>
          <p className="payment-options">Corporate training options and installment plans available</p>
        </div>
      </div>
    </div>
  );
};

export default AdvancedApexBrochure;